import { STRATEGIES, STRATEGY_BY_SLUG } from "../data/strategies";
import { PERSPECTIVES, PERSPECTIVE_BY_SLUG } from "../data/perspectives";
import { NEWS, NEWS_BY_SLUG } from "../data/news";

/**
 * Route → metadata registry (pure module — no React, no path aliases — so
 * the post-build injector in scripts/inject-meta.js can load it in Node).
 *
 * Titles and descriptions are drawn from each page's existing visible
 * content. No claims are made here that are not already made on the page.
 */
export const SITE = "https://krprivatecapital.com";
export const SITE_NAME = "Kellermann Reid & Co.";
export const OG_IMAGE = `${SITE}/brand/og-image.jpg`;

const BRAND = SITE_NAME;
const t = (page) => `${page} | ${BRAND}`;

const DEFAULT_DESCRIPTION =
  "Kellermann Reid & Co. is a private markets investment house focused on capital formation, private credit, real estate debt, fund-structured opportunities, and selective investor access.";

const clip = (text, max = 200) => {
  if (!text || text.length <= max) return text;
  const cut = text.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
};

const firstSentence = (text) => {
  const idx = text.indexOf(". ");
  return idx > 0 && idx < 220 ? text.slice(0, idx + 1) : clip(text, 200);
};

export const canonicalFor = (pathname) => {
  const clean = pathname.replace(/\/+$/, "");
  return `${SITE}${clean || "/"}`;
};

const STATIC = {
  "/": {
    title: `${BRAND} | Private Markets Investment House`,
    description: DEFAULT_DESCRIPTION,
    crumb: null,
  },
  "/strategies": {
    title: t("Investment Strategies"),
    description:
      "Four mandates — Industrial Credit, Bridge Capital & Special Situations, Opportunistic Industrial Equity and Structured Capital Solutions — supported by a vehicle architecture calibrated to sophisticated institutional capital.",
    crumb: "Investment Strategies",
  },
  "/funds": {
    title: t("Fund Architecture"),
    description:
      "The platform's intended vehicle architecture — funds, co-investment channels, business lines and separately managed accounts — calibrated to the duration and governance preferences of institutional capital. Vehicle names are working titles, subject to documentation and authorisation.",
    crumb: "Fund Architecture",
  },
  "/capital-pathways": {
    title: t("Selective Capital Pathways"),
    description:
      "A mandate-led engagement framework across four documented pathways — Direct Platform Capital, Investor Access, Fund-Structured Solutions and Coordinated Introductions. Kellermann Reid & Co. does not act as a placement agent or broker-dealer.",
    crumb: "Selective Capital Pathways",
  },
  "/family-office-investment-solutions": {
    title: t("Family Office Investment Solutions"),
    description:
      "Mandate-led private-market origination, screening, diligence coordination and transaction structuring for select family offices and strategic capital partners. One global service; the family retains full discretion over every investment decision.",
    type: "article",
    crumb: "Family Office Investment Solutions",
  },
  "/team": {
    title: t("Team"),
    description:
      "A senior partnership supported by a documented Investment Committee, an Operating Partner Network and independent oversight — founding partners, partners, executive leadership, regional coverage and senior advisors.",
    crumb: "Team",
  },
  "/partnership": {
    title: t("The Partnership"),
    description:
      "Co-founded by Marcel Kellermann and Chris Reid, with Rachel Yang as Partner for Asia-Pacific and Ryan Landry as Partner for Strategic Capital. Full partner biographies and the platform's governance framework.",
    crumb: "The Partnership",
    parent: ["Team", "/team"],
  },
  "/leadership": {
    title: t("Executive Leadership"),
    description:
      "Senior executives who lead defined functions in support of the partnership — including Sophus Kludt, Head of Capital Formation. Leadership members are not partners and do not hold GP economics.",
    crumb: "Executive Leadership",
    parent: ["Team", "/team"],
  },
  "/regional-coverage": {
    title: t("Regional Coverage"),
    description:
      "Regionally-based representatives who are the partnership's on-the-ground contact point in a market — including Sven Massenhove, Gulf Region Lead, Dubai (DIFC). Engaged on documented, non-executive terms.",
    crumb: "Regional Coverage",
    parent: ["Team", "/team"],
  },
  "/advisors": {
    title: t("Senior Advisors"),
    description:
      "Non-executive Senior Advisors contributing commercial-banking, structuring and credit-risk pattern recognition to the partnership — Alistair Wood and Angelo Barbati. Not partners; not authorised to act on behalf of the firm.",
    crumb: "Senior Advisors",
    parent: ["Team", "/team"],
  },
  "/gulf-investors": {
    title: t("For Gulf Investors"),
    description:
      "How limited partners in the United Arab Emirates and the wider GCC engage with Kellermann Reid & Co. — by introduction, through a named regional contact in Dubai (DIFC), on the same documented terms as every institutional relationship.",
    crumb: "For Gulf Investors",
  },
  "/contact": {
    title: t("Contact"),
    description:
      "General correspondence, press enquiries and counterparty introductions. Headquartered in Austin, Texas, with coverage across New York, London, Geneva, Dubai, Singapore and Hong Kong.",
    crumb: "Contact",
  },
  "/responsible-capital": {
    title: t("Responsible Capital"),
    description:
      "Stewardship treated as an underwriting input rather than a communications exercise — environmental, social and governance factors integrated into diligence, structuring and ongoing engagement.",
    crumb: "Responsible Capital",
  },
  "/news": {
    title: t("Newsroom"),
    description:
      "Formation milestones, platform updates and counterparty engagement notes from Kellermann Reid & Co. Written for sophisticated readers; not investment advice.",
    crumb: "Newsroom",
  },
  "/perspectives": {
    title: t("Perspectives"),
    description:
      "Considered papers across macro, underwriting and sector themes from Kellermann Reid & Co. Written for sophisticated readers; not investment advice.",
    crumb: "Perspectives",
  },
  "/press-kit": {
    title: t("Press Kit"),
    description:
      "Brand assets, firm facts and a short boilerplate for media, conference programmes and counterparty introductions.",
    crumb: "Press Kit",
  },
  "/careers": {
    title: t("Careers"),
    description:
      "Kellermann Reid & Co. does not maintain an open job board. The partnership grows through documented, considered appointments — initiated by introduction and evaluated through a confidential process.",
    crumb: "Careers",
  },
  "/disclosures": {
    title: t("Disclosures"),
    description:
      "A consolidated statement of the platform's regulatory posture, working engagements with Cayman service providers, and the forward-looking nature of vehicle-level descriptions.",
    crumb: "Disclosures",
  },
  "/legal": {
    title: t("Legal Notices"),
    description:
      "Legal notices and disclaimers governing use of the Kellermann Reid & Co. website and the materials published on it.",
    crumb: "Legal Notices",
  },
  "/privacy": {
    title: t("Privacy Notice"),
    description:
      "How Kellermann Reid & Co. handles personal data submitted through this website, including investor access and contact enquiries.",
    crumb: "Privacy Notice",
  },
  "/cookies": {
    title: t("Cookies Notice"),
    description:
      "How this website uses cookies and similar technologies, and the choices available to visitors.",
    crumb: "Cookies Notice",
  },
};

/** Every indexable public route, in sitemap order. */
export const PUBLIC_ROUTES = [
  "/",
  "/strategies",
  ...STRATEGIES.map((s) => `/strategies/${s.slug}`),
  "/funds",
  "/capital-pathways",
  "/family-office-investment-solutions",
  "/team",
  "/partnership",
  "/advisors",
  "/leadership",
  "/regional-coverage",
  "/gulf-investors",
  "/responsible-capital",
  "/perspectives",
  ...PERSPECTIVES.map((p) => `/perspectives/${p.slug}`),
  "/news",
  ...NEWS.map((n) => `/news/${n.slug}`),
  "/press-kit",
  "/careers",
  "/contact",
  "/disclosures",
  "/legal",
  "/privacy",
  "/cookies",
];

const ORG = {
  "@type": "Organization",
  name: SITE_NAME,
  url: `${SITE}/`,
  logo: `${SITE}/brand/og-image.jpg`,
};

const breadcrumbs = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [["Home", "/"], ...items].map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: canonicalFor(path),
  })),
});

const article = ({ headline, description, path, section, contributor }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  url: canonicalFor(path),
  mainEntityOfPage: canonicalFor(path),
  image: OG_IMAGE,
  articleSection: section,
  inLanguage: "en",
  isAccessibleForFree: true,
  author: ORG,
  publisher: ORG,
  ...(contributor
    ? {
        contributor: {
          "@type": "Person",
          name: contributor.name,
          jobTitle: contributor.role,
          url: canonicalFor(contributor.href.split("#")[0]),
        },
      }
    : {}),
});

export const resolveRouteMeta = (pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  const canonical = canonicalFor(path);

  if (STATIC[path]) {
    const s = STATIC[path];
    const crumbs = s.crumb
      ? [...(s.parent ? [s.parent] : []), [s.crumb, path]]
      : null;
    return {
      title: s.title,
      description: s.description,
      type: s.type,
      canonical,
      structuredData: crumbs ? [breadcrumbs(crumbs)] : [],
    };
  }

  // Internal preview variant — not a public page.
  if (path === "/v2") {
    return {
      ...STATIC["/"],
      canonical: canonicalFor("/"),
      noindex: true,
      structuredData: [],
    };
  }

  const [, section, slug] = path.split("/");
  if (section === "strategies" && STRATEGY_BY_SLUG[slug]) {
    const s = STRATEGY_BY_SLUG[slug];
    return {
      title: t(s.title),
      description: s.summary,
      canonical,
      structuredData: [
        breadcrumbs([
          ["Investment Strategies", "/strategies"],
          [s.title, path],
        ]),
      ],
    };
  }
  if (section === "perspectives" && PERSPECTIVE_BY_SLUG[slug]) {
    const p = PERSPECTIVE_BY_SLUG[slug];
    const description = clip(p.excerpt, 220);
    return {
      title: `${p.title} | KR Perspectives`,
      description,
      canonical,
      type: "article",
      structuredData: [
        breadcrumbs([["Perspectives", "/perspectives"], [p.title, path]]),
        article({
          headline: p.title,
          description: p.excerpt,
          path,
          section: p.tag,
          contributor: p.contributor,
        }),
      ],
    };
  }
  if (section === "news" && NEWS_BY_SLUG[slug]) {
    const n = NEWS_BY_SLUG[slug];
    return {
      title: `${n.title} | KR Newsroom`,
      description: firstSentence(n.body),
      canonical,
      type: "article",
      structuredData: [
        breadcrumbs([["Newsroom", "/news"], [n.title, path]]),
        article({
          headline: n.title,
          description: n.body,
          path,
          section: n.kind,
        }),
      ],
    };
  }

  return {
    title: t("Page Not Found"),
    description: DEFAULT_DESCRIPTION,
    canonical,
    noindex: true,
    structuredData: [],
  };
};
